import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaMapMarkerAlt, FaPhone, FaStore, FaUser, FaChartLine, FaGlobe, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { storesAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Stores = () => {
    const [stores, setStores] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showVendorsModal, setShowVendorsModal] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const isSubmitting = useRef(false);
    const createButtonRef = useRef(null);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        country: 'USA',
        postal_code: '',
        phone: '',
        email: ''
    });

    useEffect(() => {
        fetchStores();
    }, [pagination.page]);

    useEffect(() => {
        if (Array.isArray(stores)) {
            const filtered = stores.filter(store => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    store.name?.toLowerCase().includes(searchLower) ||
                    store.code?.toLowerCase().includes(searchLower) ||
                    store.city?.toLowerCase().includes(searchLower) ||
                    store.address?.toLowerCase().includes(searchLower) ||
                    store.state?.toLowerCase().includes(searchLower) ||
                    (store.phone && store.phone.toLowerCase().includes(searchLower))
                );
            });
            setFilteredStores(filtered);
        }
    }, [searchTerm, stores]);

    const fetchStores = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await storesAPI.list({ page: pagination.page, limit: pagination.limit });

            console.log('Stores API Response:', response.data);

            let storesData = [];
            let paginationData = {};

            // Handle the specific response structure from the API
            if (response.data && response.data.status === 'success' && response.data.data) {
                // Structure: { status: "success", data: { stores: [...], meta: {...} } }
                if (response.data.data.stores && Array.isArray(response.data.data.stores)) {
                    storesData = response.data.data.stores;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                // Alternative structure: { data: [...] }
                storesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                // Direct array response
                storesData = response.data;
            } else if (response.data && response.data.stores && Array.isArray(response.data.stores)) {
                // Structure: { stores: [...], meta: {...} }
                storesData = response.data.stores;
                if (response.data.meta) {
                    paginationData = response.data.meta;
                }
            }

            if (!Array.isArray(storesData)) {
                console.error('Invalid stores data format:', response.data);
                storesData = [];
            }

            // Process stores to ensure vendors_count is available and map fields correctly
            const processedStores = storesData.map(store => ({
                id: store.id,
                name: store.name || 'Unnamed Store',
                code: store.code || '',
                address: store.address || '',
                city: store.city || '',
                state: store.state || '',
                country: store.country || 'USA',
                postal_code: store.postal_code || store.zip_code || '', // Handle both postal_code and zip_code
                phone: store.phone || '',
                email: store.email || '',
                status: store.is_active === 1 ? 'active' : (store.status || 'active'),
                is_active: store.is_active !== undefined ? store.is_active : 1,
                vendors_count: store.vendors_count || (store.vendors ? store.vendors.length : 0) || 0,
                vendors: store.vendors || [],
                created_at: store.created_at,
                updated_at: store.updated_at
            }));

            console.log('Processed stores:', processedStores);
            setStores(processedStores);
            setFilteredStores(processedStores);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || processedStores.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || processedStores.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching stores:', err);
            setError(err.response?.data?.message || 'Failed to fetch stores');
            setStores([]);
            setFilteredStores([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();

        if (isSubmitting.current) {
            console.log('Create submission already in progress, preventing duplicate');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name?.trim(),
                code: formData.code?.trim(),
                address: formData.address?.trim(),
                city: formData.city?.trim(),
                state: formData.state?.trim(),
                country: formData.country?.trim() || 'USA',
                postal_code: formData.postal_code?.trim(),
                phone: formData.phone?.trim() || '',
                email: formData.email?.trim() || ''
            };

            // Validate required fields
            if (!payload.name || !payload.code || !payload.address || !payload.city ||
                !payload.state || !payload.country || !payload.postal_code) {
                setError('Please fill in all required fields');
                return;
            }

            console.log('Creating store with payload:', payload);

            await storesAPI.create(payload);

            setSuccessMessage('Store created successfully!');
            setShowCreateModal(false);
            resetForm();
            await fetchStores();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error creating store:', err);

            let errorMsg = 'Failed to create store';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.message) {
                errorMsg = err.message;
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        if (isSubmitting.current) {
            console.log('Update submission already in progress, preventing duplicate');
            return;
        }

        if (!selectedStore?.id) {
            setError('Store ID is missing');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name?.trim(),
                code: formData.code?.trim(),
                address: formData.address?.trim(),
                city: formData.city?.trim(),
                state: formData.state?.trim(),
                country: formData.country?.trim() || 'USA',
                postal_code: formData.postal_code?.trim(),
                phone: formData.phone?.trim() || '',
                email: formData.email?.trim() || ''
            };

            // Validate required fields
            if (!payload.name || !payload.code || !payload.address || !payload.city ||
                !payload.state || !payload.country || !payload.postal_code) {
                setError('Please fill in all required fields');
                return;
            }

            console.log('Updating store with ID:', selectedStore.id);
            console.log('Update payload:', payload);

            await storesAPI.update(selectedStore.id, payload);

            setSuccessMessage('Store updated successfully!');
            setShowEditModal(false);
            resetForm();
            setSelectedStore(null);
            setIsEditing(false);
            await fetchStores();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error updating store:', err);

            let errorMsg = 'Failed to update store';

            if (err.response) {
                console.error('Update Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    }
                }

                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot update this store.';
                } else if (err.response.status === 404) {
                    errorMsg = 'Store not found. It may have been deleted.';
                } else if (err.response.status === 400) {
                    errorMsg = err.response.data.message || 'Invalid store data. Please check your inputs.';
                } else if (err.response.status === 422) {
                    errorMsg = 'Validation error. Please check your inputs.';
                }
            } else if (err.request) {
                errorMsg = 'No response from server. Please check your connection.';
            } else {
                errorMsg = err.message || 'Failed to update store';
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleDelete = async () => {
        if (!selectedStore?.id) {
            setError('Store ID is missing');
            return;
        }

        if (isSubmitting.current) {
            console.log('Delete submission already in progress, preventing duplicate');
            return;
        }

        try {
            isSubmitting.current = true;
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Attempting to delete store with ID:', selectedStore.id);

            await storesAPI.delete(selectedStore.id);

            setSuccessMessage('Store deleted successfully!');
            setShowDeleteModal(false);
            setSelectedStore(null);
            await fetchStores();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error deleting store:', err);

            let errorMsg = 'Failed to delete store';

            if (err.response) {
                console.error('Delete Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    }
                }

                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot delete this store.';
                } else if (err.response.status === 404) {
                    errorMsg = 'Store not found. It may have already been deleted.';
                } else if (err.response.status === 400) {
                    errorMsg = err.response.data.message || 'Cannot delete store with existing inventory.';
                }
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const handleStatusChange = async (storeId, currentStatus) => {
        if (isSubmitting.current) return;

        try {
            isSubmitting.current = true;
            // Convert status to is_active (1 for active, 0 for inactive)
            const newIsActive = currentStatus === 'active' ? 0 : 1;
            await storesAPI.updateStatus(storeId, newIsActive);
            await fetchStores();
        } catch (err) {
            console.error('Error changing store status:', err);
            setError('Failed to change store status');
        } finally {
            setTimeout(() => {
                isSubmitting.current = false;
            }, 500);
        }
    };

    const openEditModal = (store) => {
        console.log('Opening edit modal for store:', store);

        setSelectedStore(store);

        setFormData({
            name: store.name || '',
            code: store.code || '',
            address: store.address || '',
            city: store.city || '',
            state: store.state || '',
            country: store.country || 'USA',
            postal_code: store.postal_code || '',
            phone: store.phone || '',
            email: store.email || ''
        });

        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (store) => {
        setSelectedStore(store);
        setShowDeleteModal(true);
        setError('');
    };

    const openVendorsModal = (store) => {
        setSelectedStore(store);
        setShowVendorsModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            address: '',
            city: '',
            state: '',
            country: 'USA',
            postal_code: '',
            phone: '',
            email: ''
        });
        setIsEditing(false);
        setSelectedStore(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
        isSubmitting.current = false;
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getStatusBadge = (status) => {
        // Handle both string status and is_active number
        if (status === 'active' || status === 1 || status === true) {
            return <Badge bg="success">Active</Badge>;
        } else if (status === 'inactive' || status === 0 || status === false) {
            return <Badge bg="secondary">Inactive</Badge>;
        } else if (status === 'pending') {
            return <Badge bg="warning">Pending</Badge>;
        } else {
            return <Badge bg="info">Active</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatLocation = (store) => {
        const parts = [];
        if (store.city) parts.push(store.city);
        if (store.state) parts.push(store.state);
        if (store.postal_code) parts.push(store.postal_code);
        return parts.length > 0 ? parts.join(', ') : 'Location not specified';
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading && stores.length === 0) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading stores...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Stores Management</h1>
                    <p className="text-muted mb-0">Manage physical store locations and details</p>
                </Col>
                <Col xs="auto">
                    <Button
                        ref={createButtonRef}
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                        disabled={actionLoading}
                    >
                        <FaPlus className="me-2" />
                        Create Store
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')} className="mb-4">
                    {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search stores by name, code, city, or address..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                    disabled={actionLoading}
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredStores.length : stores.length}</strong> stores
                                </small>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                    disabled={actionLoading}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Store</th>
                                <th>Location</th>
                                <th>Contact</th>
                                <th>Vendors</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStores.length > 0 ? (
                                filteredStores.map(store => (
                                    <tr key={store.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaStore className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{store.name || 'N/A'}</div>
                                                    <small className="text-muted">
                                                        Code: {store.code || 'N/A'}
                                                    </small>
                                                    <div className="text-muted small">
                                                        ID: {store.id?.substring(0, 12)}...
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div className="fw-medium">
                                                    <FaMapMarkerAlt className="me-1" size={12} />
                                                    {store.address ? `${store.address.substring(0, 40)}${store.address.length > 40 ? '...' : ''}` : 'No address'}
                                                </div>
                                                <div className="text-muted">
                                                    {formatLocation(store)}
                                                </div>
                                                {store.country && (
                                                    <div className="text-muted">
                                                        <FaGlobe className="me-1" size={10} />
                                                        {store.country}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                {store.phone && (
                                                    <div className="mb-1">
                                                        <FaPhone className="me-1" size={12} />
                                                        {store.phone}
                                                    </div>
                                                )}
                                                {store.email && (
                                                    <div>
                                                        <FaUser className="me-1" size={12} />
                                                        {store.email}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <Button
                                                variant="link"
                                                className="p-0 text-decoration-none d-flex align-items-center"
                                                onClick={() => openVendorsModal(store)}
                                                disabled={store.vendors_count === 0}
                                            >
                                                <Badge
                                                    bg={store.vendors_count > 0 ? "info" : "secondary"}
                                                    className="cursor-pointer"
                                                    style={{ cursor: store.vendors_count > 0 ? 'pointer' : 'default' }}
                                                >
                                                    {store.vendors_count} {store.vendors_count === 1 ? 'Vendor' : 'Vendors'}
                                                </Badge>
                                                {store.vendors_count > 0 && (
                                                    <FaInfoCircle className="ms-1 text-info" size={12} />
                                                )}
                                            </Button>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(store.is_active !== undefined ? store.is_active : store.status)}
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="ms-2 p-0"
                                                    onClick={() => handleStatusChange(store.id, store.is_active === 1 ? 'active' : 'inactive')}
                                                    title={`Change to ${store.is_active === 1 ? 'inactive' : 'active'}`}
                                                    disabled={actionLoading}
                                                >
                                                    {store.is_active === 1 ? (
                                                        <FaTrash className="text-danger" size={14} />
                                                    ) : (
                                                        <FaChartLine className="text-success" size={14} />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(store.created_at)}</div>
                                                <div className="text-muted">Updated: {formatDate(store.updated_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(store)}
                                                    title="Edit Store"
                                                    disabled={actionLoading}
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(store)}
                                                    title="Delete Store"
                                                    disabled={actionLoading}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No stores found matching "{searchTerm}"</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaStore className="h3 mb-3" />
                                                    <p>No stores found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {stores.length} of {pagination.total} stores
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {stores.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Stores</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {stores.filter(s => s.is_active === 1).length}
                                </div>
                                <small>Active Stores</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {stores.filter(s => s.vendors_count > 0).length}
                                </div>
                                <small>Stores with Vendors</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {stores.filter(s => s.phone).length}
                                </div>
                                <small>Stores with Phone</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Store"
                onSubmit={handleCreate}
                submitText="Create Store"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter store name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                required
                                placeholder="e.g., S001"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        placeholder="Enter full address"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>City *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                required
                                placeholder="Enter city"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>State *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                required
                                placeholder="Enter state"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Postal Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.postal_code}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                required
                                placeholder="Enter postal code"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Country *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.country}
                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                required
                                placeholder="Enter country"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Enter store email"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Store: ${selectedStore?.name || ''}`}
                onSubmit={handleUpdate}
                submitText="Update Store"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter store name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                required
                                placeholder="e.g., S001"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        placeholder="Enter full address"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>City *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                required
                                placeholder="Enter city"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>State *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                required
                                placeholder="Enter state"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Postal Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.postal_code}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                required
                                placeholder="Enter postal code"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Country *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.country}
                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                required
                                placeholder="Enter country"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Enter store email"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Store"
                message={
                    <div>
                        <p>Are you sure you want to delete store <strong>"{selectedStore?.name}"</strong>?</p>
                        <p className="text-danger mb-0">This will also remove associated inventory. This action cannot be undone.</p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
            />

            {/* Vendors Modal */}
            <Modal show={showVendorsModal} onHide={() => setShowVendorsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaStore className="me-2 text-warning" />
                        Vendors for {selectedStore?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedStore?.vendors && selectedStore.vendors.length > 0 ? (
                        <div className="vendor-list">
                            {selectedStore.vendors.map(vendor => (
                                <Card key={vendor.id} className="mb-3 border-0 shadow-sm">
                                    <Card.Body className="d-flex align-items-center">
                                        <div
                                            className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '50px', height: '50px' }}
                                        >
                                            <FaStore className="text-white" />
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">{vendor.name}</h6>
                                            <div className="d-flex flex-wrap gap-3 small text-muted">
                                                <span><FaUser className="me-1" size={12} /> {vendor.email}</span>
                                                {vendor.phone && (
                                                    <span><FaPhone className="me-1" size={12} /> {vendor.phone}</span>
                                                )}
                                                {vendor.website && (
                                                    <span><FaGlobe className="me-1" size={12} /> {vendor.website}</span>
                                                )}
                                            </div>
                                            {vendor.description && (
                                                <div className="mt-2 small text-muted">
                                                    {vendor.description}
                                                </div>
                                            )}
                                        </div>
                                        <Badge bg={vendor.status === 'active' ? 'success' : 'secondary'}>
                                            {vendor.status || 'active'}
                                        </Badge>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted py-4">
                            No vendors associated with this store.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVendorsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Stores;